<script setup>
const toast = useToast();

const effects = ref([]);
const loadError = ref("");
const loading = ref(true);

const ALL_FILTER = "__all__";

const searchQuery = ref("");
const channelFilter = ref(ALL_FILTER);
const featureFilter = ref(ALL_FILTER);
const qualityFilter = ref(ALL_FILTER);
const durationFilter = ref(ALL_FILTER);
const imageCountFilter = ref(ALL_FILTER);
const typeLabelFilter = ref(ALL_FILTER);

const detailOpen = ref(false);
const activeEffect = ref(null);
const modalVideoRef = ref(null);

function list(value) {
  if (!value) return [];
  return Array.isArray(value) ? value : [value];
}

function textList(value) {
  return list(value).filter(Boolean).join(" | ");
}

function getName(effect) {
  return effect.name_zh || effect.display_name || effect.name_en || "";
}

function getChannels(effect) {
  const channels = list(effect.channels_zh).length
    ? list(effect.channels_zh)
    : list(effect.channels_en);
  return channels.length ? channels : ["未分组"];
}

function primaryChannel(effect) {
  return getChannels(effect)[0] || "未分组";
}

function searchText(effect) {
  return [
    effect.template_id,
    effect.name_zh,
    effect.name_en,
    effect.display_name,
    effect.prompt_zh,
    effect.prompt_en,
    effect.topic_requirements,
    textList(effect.qualities),
    textList(effect.support_duration_seconds),
    textList(effect.supported_features),
    textList(effect.channels_zh),
    textList(effect.channels_en),
    effect.app_thumbnail_url,
    effect.app_thumbnail_video_url,
    effect.audio_url,
  ]
    .join(" ")
    .toLowerCase();
}

function isAllFilter(value) {
  return !value || value === ALL_FILTER;
}

function effectMatchesFilters(effect) {
  const q = searchQuery.value.trim().toLowerCase();
  if (q && !searchText(effect).includes(q)) return false;
  if (
    !isAllFilter(featureFilter.value) &&
    !list(effect.supported_features).includes(featureFilter.value)
  ) {
    return false;
  }
  if (
    !isAllFilter(qualityFilter.value) &&
    !list(effect.qualities).includes(qualityFilter.value)
  ) {
    return false;
  }
  if (
    !isAllFilter(durationFilter.value) &&
    !list(effect.support_duration_seconds).includes(Number(durationFilter.value))
  ) {
    return false;
  }
  if (
    !isAllFilter(imageCountFilter.value) &&
    String(effect.upload_image_count || "") !== imageCountFilter.value
  ) {
    return false;
  }
  if (
    !isAllFilter(typeLabelFilter.value) &&
    String(effect.type_label || effect.type || "") !== typeLabelFilter.value
  ) {
    return false;
  }
  return true;
}

function sortedOptions(values, suffix = "") {
  return [...values]
    .sort((a, b) =>
      String(a).localeCompare(String(b), undefined, { numeric: true })
    )
    .map((value) => ({
      label: String(value) + suffix,
      value: String(value),
    }));
}

function buildSelectItems(allLabel, options, suffix = "") {
  return [
    { label: allLabel, value: ALL_FILTER },
    ...sortedOptions(options, suffix),
  ];
}

const filterSets = computed(() => {
  const channels = new Set();
  const features = new Set();
  const qualities = new Set();
  const durations = new Set();
  const counts = new Set();
  const typeLabels = new Set();

  for (const effect of effects.value) {
    getChannels(effect).forEach((v) => channels.add(v));
    list(effect.supported_features).forEach((v) => features.add(v));
    list(effect.qualities).forEach((v) => qualities.add(v));
    list(effect.support_duration_seconds).forEach((v) => durations.add(v));
    if (effect.upload_image_count) counts.add(String(effect.upload_image_count));
    if (effect.type_label) typeLabels.add(String(effect.type_label));
  }

  return { channels, features, qualities, durations, counts, typeLabels };
});

const metaItems = computed(() => {
  const { channels } = filterSets.value;
  const videoCount = effects.value.filter((e) => e.app_thumbnail_video_url).length;
  const audioCount = effects.value.filter((e) => e.audio_url).length;
  return [
    `总数：${effects.value.length}`,
    `频道：${channels.size}`,
    `预览视频：${videoCount}`,
    `音频：${audioCount}`,
    "数据：pixverse_effects.json",
  ];
});

const channelItems = computed(() =>
  buildSelectItems("全部频道", filterSets.value.channels)
);
const featureItems = computed(() =>
  buildSelectItems("全部能力", filterSets.value.features)
);
const qualityItems = computed(() =>
  buildSelectItems("全部分辨率", filterSets.value.qualities)
);
const durationItems = computed(() =>
  buildSelectItems("全部时长", filterSets.value.durations, "s")
);
const imageCountItems = computed(() =>
  buildSelectItems("全部图片数", filterSets.value.counts, " 张图")
);
const typeLabelItems = computed(() =>
  buildSelectItems("全部类型", filterSets.value.typeLabels)
);

const visibleGroups = computed(() => {
  const groups = new Map();
  const selectedChannel = isAllFilter(channelFilter.value)
    ? ""
    : channelFilter.value;

  for (const effect of effects.value) {
    if (!effectMatchesFilters(effect)) continue;
    const channels = getChannels(effect);
    if (selectedChannel && !channels.includes(selectedChannel)) continue;
    const name = selectedChannel || primaryChannel(effect);
    if (!groups.has(name)) groups.set(name, []);
    groups.get(name).push(effect);
  }

  return [...groups.entries()]
    .sort((a, b) => a[0].localeCompare(b[0], undefined, { numeric: true }))
    .map(([name, items]) => ({ name, items }));
});

const shownCount = computed(() =>
  visibleGroups.value.reduce((sum, group) => sum + group.items.length, 0)
);

function currentRequestShape(effect) {
  const imageCount = Number(effect.upload_image_count || effect.effect_type || 1);
  return imageCount > 1
    ? "multi-image template, use img_ids"
    : "single-image template, use img_id";
}

function paramRows(effect) {
  const imageCount = Number(effect.upload_image_count || effect.effect_type || 1);
  const qualityOptions = list(effect.qualities);
  const durationOptions = list(effect.support_duration_seconds);
  const featureOptions = list(effect.supported_features);

  const rows = [
    {
      field: "template_id",
      required: "required",
      current: effect.template_id,
      options: effect.template_id,
      note: "Effect/template ID. It must be activated in PixVerse Platform before use.",
    },
    {
      field: imageCount > 1 ? "img_ids" : "img_id",
      required: "required",
      current: imageCount > 1 ? `[] length = ${imageCount}` : "",
      options:
        imageCount > 1
          ? `${imageCount} uploaded image ids`
          : "one uploaded image id",
      note:
        imageCount > 1
          ? "Use this for multi-image templates. The number of IDs should match upload_image_count."
          : "Use this for single-image templates. Value comes from POST /openapi/v2/image/upload.",
    },
    {
      field: imageCount > 1 ? "img_id" : "img_ids",
      required: "empty",
      current: "",
      options: "",
      note:
        imageCount > 1
          ? "Leave empty when img_ids is used."
          : "Leave empty when img_id is used.",
    },
    {
      field: "quality",
      required: "required",
      current: qualityOptions[0] || "",
      options: textList(qualityOptions),
      note: "Must use one of the supported qualities for this effect.",
    },
    {
      field: "duration",
      required: "required",
      current: durationOptions[0] || 5,
      options: textList(durationOptions.map((x) => `${x}s`)),
      note: "Use one of support_duration_seconds. Template duration may still follow the template itself.",
    },
    {
      field: "model",
      required: "required",
      current: "v6",
      options: "v3.5 | v4 | v4.5 | v5 | v5.5 | v5.6 | v6 | c1",
      note: "Official docs recommend v6. When template_id is provided, model is ignored.",
    },
    {
      field: "prompt",
      required: "required",
      current: "",
      options: "<= 5000 characters",
      note: "Can be empty. When template_id is provided, prompt is ignored.",
    },
    {
      field: "seed",
      required: "optional",
      current: "",
      options: "0 - 2147483647",
      note: "Random seed. Leave empty if not needed.",
    },
    {
      field: "webhook_id",
      required: "optional",
      current: "",
      options: "Webhook ID from PixVerse Platform",
      note: "Set this to receive async callbacks. Keep polling as fallback.",
    },
    {
      field: "sound_effect_switch",
      required: "optional",
      current: "",
      options: "true | false",
      note: "Supported with template_id. Default false.",
    },
    {
      field: "sound_effect_content",
      required: "optional/empty",
      current: "",
      options: "string",
      note: "Used when sound_effect_switch=true. Empty means auto generation.",
    },
    {
      field: "generate_audio_switch",
      required: "optional",
      current: "",
      options: "true | false",
      note: "Supported in v5.5/v5.6/v6/c1. Controls generated audio. Default false.",
    },
    {
      field: "generate_multi_clip_switch",
      required: "optional",
      current: "",
      options: "true | false",
      note: "Supported in v5.5/v6. Controls multi-clip mode. Default false.",
    },
    {
      field: "lip_sync_switch",
      required: "optional",
      current: "",
      options: "true | false",
      note: "Supported in v5 and below. Default false.",
    },
    {
      field: "lip_sync_tts_content",
      required: "optional/empty",
      current: "",
      options: "~140 UTF-8 characters",
      note: "Used for lip sync TTS when enabled.",
    },
    {
      field: "lip_sync_tts_speaker_id",
      required: "optional/empty",
      current: "",
      options: "speaker id from TTS speaker list",
      note: "Used for lip sync TTS when enabled.",
    },
    {
      field: "motion_mode",
      required: "optional/empty",
      current: "",
      options: "normal or supported API value",
      note: "Shown in official image-to-video example. Leave empty for template defaults.",
    },
    {
      field: "camera_movement",
      required: "optional/empty",
      current: "",
      options: "camera movement API value",
      note: "Leave empty unless you intentionally apply camera movement.",
    },
  ];

  return { imageCount, qualityOptions, durationOptions, featureOptions, rows };
}

function requestExample(effect) {
  const data = paramRows(effect);
  const payload = {
    duration: data.durationOptions[0] || 5,
    model: "v6",
    prompt: "",
    quality: data.qualityOptions[0] || "720p",
    template_id: effect.template_id,
  };
  if (data.imageCount > 1) {
    payload.img_ids = Array.from({ length: data.imageCount }, () => 0);
  } else {
    payload.img_id = 0;
  }
  payload.seed = null;
  payload.webhook_id = "";
  payload.sound_effect_switch = null;
  payload.sound_effect_content = "";
  payload.generate_audio_switch = null;
  payload.generate_multi_clip_switch = null;
  payload.lip_sync_switch = null;
  payload.lip_sync_tts_content = "";
  payload.lip_sync_tts_speaker_id = "";
  return JSON.stringify(payload, null, 2);
}

const activeParamRows = computed(() =>
  activeEffect.value ? paramRows(activeEffect.value).rows : []
);

const activeRequestExample = computed(() =>
  activeEffect.value ? requestExample(activeEffect.value) : ""
);

const activeCreditRows = computed(() =>
  activeEffect.value ? list(activeEffect.value.credit_consume) : []
);

const mediaLinkRows = computed(() => {
  if (!activeEffect.value) return [];
  const effect = activeEffect.value;
  return [
    { label: "app 封面图", url: effect.app_thumbnail_url },
    { label: "app 预览视频", url: effect.app_thumbnail_video_url },
    { label: "app GIF", url: effect.app_thumbnail_gif_url },
    { label: "web 封面图", url: effect.web_thumbnail_url },
    { label: "web 预览视频", url: effect.web_thumbnail_video_url },
    { label: "web GIF", url: effect.web_thumbnail_gif_url },
    { label: "音频", url: effect.audio_url },
  ].filter((row) => row.url);
});

const activeVideoUrl = computed(() => {
  if (!activeEffect.value) return "";
  return (
    activeEffect.value.app_thumbnail_video_url ||
    activeEffect.value.web_thumbnail_video_url ||
    ""
  );
});

const activeImageUrl = computed(() => {
  if (!activeEffect.value) return "";
  return (
    activeEffect.value.app_thumbnail_url ||
    activeEffect.value.web_thumbnail_url ||
    ""
  );
});

function markerColor(marker) {
  if (marker === "hot") return "error";
  if (marker === "new") return "success";
  return "neutral";
}

function openDetail(effect) {
  activeEffect.value = effect;
  detailOpen.value = true;
  nextTick(() => {
    modalVideoRef.value?.play?.().catch(() => {});
  });
}

function closeDetail() {
  modalVideoRef.value?.pause?.();
  detailOpen.value = false;
}

function onDetailOpenChange(open) {
  if (!open) {
    modalVideoRef.value?.pause?.();
    activeEffect.value = null;
  }
}

async function copyActiveId() {
  if (!activeEffect.value) return;
  try {
    await navigator.clipboard.writeText(String(activeEffect.value.template_id));
    toast.add({ title: "已复制 ID", color: "success" });
  } catch {
    toast.add({ title: "复制失败", color: "error" });
  }
}

function cardThumbnail(effect) {
  return effect.app_thumbnail_url || effect.web_thumbnail_url || "";
}

const filterSelectAttrs = {
  size: "md",
  valueKey: "value",
  labelKey: "label",
  content: { side: "bottom", align: "start", class: "z-[10050]" },
};

function releaseSelectFocus() {
  requestAnimationFrame(() => {
    const active = document.activeElement;
    if (active instanceof HTMLElement) {
      active.blur();
    }
  });
}

async function loadEffects() {
  loading.value = true;
  loadError.value = "";
  try {
    const response = await fetch("/pixverse_effects.json", { cache: "no-store" });
    if (!response.ok) throw new Error(`HTTP ${response.status}`);
    const data = await response.json();
    if (!Array.isArray(data)) {
      throw new Error("pixverse_effects.json must be an array");
    }
    effects.value = data;
  } catch (error) {
    loadError.value = error?.message || String(error);
  } finally {
    loading.value = false;
  }
}

onMounted(loadEffects);
</script>

<template>
  <div class="min-h-screen bg-gray-950 text-gray-100">
    <header class="border-b border-gray-800 bg-gray-950 px-6 py-4">
      <h1 class="text-xl font-bold">PixVerse Effect Gallery</h1>

      <div
        v-if="loadError"
        class="mt-3 rounded-lg border border-red-900/60 bg-red-950/40 px-3 py-2 text-sm text-red-200"
      >
        无法读取
        <code class="text-sky-300">pixverse_effects.json</code>：{{ loadError }}
      </div>

      <div v-else class="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-sm text-gray-400">
        <span v-for="item in metaItems" :key="item">{{ item }}</span>
      </div>

      <div
        class="mt-4 grid grid-cols-1 gap-2 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-[minmax(260px,1.4fr)_repeat(6,minmax(140px,1fr))_auto]"
      >
        <UInput
          v-model="searchQuery"
          icon="i-lucide-search"
          placeholder="按 ID、名称、参数、主体要求、链接搜索"
          size="md"
          class="w-full xl:col-span-1 2xl:col-span-1"
        />
        <USelect
          v-model="channelFilter"
          :items="channelItems"
          placeholder="全部频道"
          class="w-full min-w-0"
          v-bind="filterSelectAttrs"
          @blur="releaseSelectFocus"
          @update:model-value="releaseSelectFocus"
        />
        <USelect
          v-model="featureFilter"
          :items="featureItems"
          placeholder="全部能力"
          class="w-full min-w-0"
          v-bind="filterSelectAttrs"
          @blur="releaseSelectFocus"
          @update:model-value="releaseSelectFocus"
        />
        <USelect
          v-model="qualityFilter"
          :items="qualityItems"
          placeholder="全部分辨率"
          class="w-full min-w-0"
          v-bind="filterSelectAttrs"
          @blur="releaseSelectFocus"
          @update:model-value="releaseSelectFocus"
        />
        <USelect
          v-model="durationFilter"
          :items="durationItems"
          placeholder="全部时长"
          class="w-full min-w-0"
          v-bind="filterSelectAttrs"
          @blur="releaseSelectFocus"
          @update:model-value="releaseSelectFocus"
        />
        <USelect
          v-model="imageCountFilter"
          :items="imageCountItems"
          placeholder="全部图片数"
          class="w-full min-w-0"
          v-bind="filterSelectAttrs"
          @blur="releaseSelectFocus"
          @update:model-value="releaseSelectFocus"
        />
        <USelect
          v-model="typeLabelFilter"
          :items="typeLabelItems"
          placeholder="全部类型"
          class="w-full min-w-0"
          v-bind="filterSelectAttrs"
          @blur="releaseSelectFocus"
          @update:model-value="releaseSelectFocus"
        />
        <span
          class="whitespace-nowrap text-sm text-gray-400 sm:col-span-2 xl:col-span-full 2xl:col-span-1 2xl:justify-self-end"
        >
          {{ shownCount }} shown / {{ visibleGroups.length }} groups
        </span>
      </div>
    </header>

    <main class="px-6 py-5 pb-10">
      <div v-if="loading" class="flex justify-center py-20">
        <UIcon name="i-lucide-loader-2" class="h-8 w-8 animate-spin text-gray-400" />
      </div>

      <div v-else-if="!loadError" class="flex flex-col gap-7">
        <section
          v-for="group in visibleGroups"
          :key="group.name"
          class="min-w-0"
        >
          <div
            class="mb-3 flex items-baseline justify-between gap-3 border-b border-gray-800 pb-2"
          >
            <h2 class="text-lg font-semibold">{{ group.name }}</h2>
            <span class="text-sm text-gray-400">
              {{ group.items.length }} effects
            </span>
          </div>

          <div
            class="grid grid-cols-[repeat(auto-fill,minmax(270px,1fr))] gap-4"
          >
            <UCard
              v-for="effect in group.items"
              :key="effect.template_id"
              class="cursor-pointer overflow-hidden border-gray-800 bg-gray-900 transition hover:border-gray-600"
              :ui="{ body: 'p-0 sm:p-0' }"
              @click="openDetail(effect)"
            >
              <div class="relative aspect-3/4 overflow-hidden bg-black">
                <img
                  v-if="cardThumbnail(effect)"
                  :src="cardThumbnail(effect)"
                  :alt="getName(effect)"
                  loading="lazy"
                  class="h-full w-full object-cover"
                />
                <div class="absolute left-2.5 top-2.5 flex flex-wrap gap-1.5">
                  <UBadge
                    v-if="effect.marker"
                    :color="markerColor(effect.marker)"
                    variant="subtle"
                    size="xs"
                  >
                    {{ effect.marker }}
                  </UBadge>
                  <UBadge color="neutral" variant="subtle" size="xs">
                    {{ effect.upload_image_count || "-" }} 图
                  </UBadge>
                </div>
                <UButton
                  v-if="effect.app_thumbnail_video_url"
                  size="xs"
                  color="neutral"
                  variant="soft"
                  class="absolute bottom-2.5 right-2.5 bg-black/60"
                  @click.stop="openDetail(effect)"
                >
                  详情
                </UButton>
              </div>

              <div class="space-y-2 p-3">
                <div
                  class="truncate text-[15px] font-bold"
                  :title="getName(effect)"
                >
                  {{ getName(effect) }}
                </div>
                <div
                  class="min-h-[18px] truncate text-xs text-gray-400"
                  :title="effect.name_en || effect.display_name || ''"
                >
                  {{ effect.name_en || effect.display_name || "" }}
                </div>
                <div class="text-xs text-gray-400">
                  ID:
                  <code class="text-sky-300">{{ effect.template_id }}</code>
                </div>

                <div class="grid grid-cols-2 gap-2">
                  <div class="rounded-md bg-gray-800/80 p-2">
                    <span class="block text-[11px] text-gray-400">时长</span>
                    <strong class="mt-0.5 block break-all text-xs">
                      {{ textList(effect.support_duration_seconds) }}s
                    </strong>
                  </div>
                  <div class="rounded-md bg-gray-800/80 p-2">
                    <span class="block text-[11px] text-gray-400">分辨率</span>
                    <strong class="mt-0.5 block break-all text-xs">
                      {{ textList(effect.qualities) }}
                    </strong>
                  </div>
                  <div class="rounded-md bg-gray-800/80 p-2">
                    <span class="block text-[11px] text-gray-400">能力</span>
                    <strong class="mt-0.5 block break-all text-xs">
                      {{ textList(effect.supported_features) }}
                    </strong>
                  </div>
                  <div class="rounded-md bg-gray-800/80 p-2">
                    <span class="block text-[11px] text-gray-400">类型</span>
                    <strong class="mt-0.5 block break-all text-xs">
                      {{ effect.type_label || effect.type || "" }}
                    </strong>
                  </div>
                </div>

                <p
                  v-if="effect.topic_requirements"
                  class="min-h-[34px] whitespace-pre-line text-xs leading-relaxed text-gray-400"
                >
                  {{ effect.topic_requirements }}
                </p>
              </div>
            </UCard>
          </div>
        </section>

        <div
          v-if="!visibleGroups.length"
          class="rounded-lg border border-gray-800 bg-gray-900/50 px-4 py-10 text-center text-sm text-gray-400"
        >
          没有匹配的 effect
        </div>
      </div>
    </main>

    <UModal
      v-model:open="detailOpen"
      :ui="{
        content:
          'top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[calc(100vw-2rem)] max-w-[1180px] max-h-[calc(100dvh-2rem)] overflow-hidden rounded-xl',
      }"
      @update:open="onDetailOpenChange"
    >
      <template #content>
        <div
          v-if="activeEffect"
          class="flex max-h-[calc(100dvh-2rem)] flex-col overflow-hidden rounded-xl bg-gray-950 text-gray-100 ring-1 ring-gray-800"
        >
          <div
            class="flex shrink-0 flex-wrap items-center justify-between gap-3 border-b border-gray-800 bg-gray-900 px-4 py-3"
          >
            <div class="min-w-0 flex-1">
              <div class="truncate text-lg font-semibold">
                {{ getName(activeEffect) }}
              </div>
              <div class="mt-0.5 truncate text-xs text-gray-400">
                {{ activeEffect.name_en || activeEffect.display_name || "" }}
                | ID {{ activeEffect.template_id }}
              </div>
            </div>
            <div class="flex shrink-0 gap-2">
              <UButton size="sm" color="neutral" variant="soft" @click="copyActiveId">
                复制 ID
              </UButton>
              <UButton size="sm" color="neutral" variant="ghost" @click="closeDetail">
                关闭
              </UButton>
            </div>
          </div>

          <div class="min-h-0 flex-1 overflow-y-auto overflow-x-hidden p-4">
            <div class="grid gap-4 xl:grid-cols-[minmax(300px,420px)_minmax(0,1fr)]">
              <div class="flex min-w-0 flex-col gap-3">
                <video
                  v-if="activeVideoUrl"
                  ref="modalVideoRef"
                  :src="activeVideoUrl"
                  controls
                  muted
                  loop
                  class="aspect-video w-full rounded-lg border border-gray-800 bg-black"
                />
                <img
                  v-if="activeImageUrl"
                  :src="activeImageUrl"
                  alt="effect thumbnail"
                  class="w-full rounded-lg border border-gray-800 bg-black"
                />
              </div>

              <div class="min-w-0 space-y-3">
                <UCard class="bg-gray-900 ring-1 ring-gray-800" :ui="{ body: 'p-4 sm:p-4' }">
                  <template #header>
                    <div class="text-sm font-semibold">调用参数</div>
                  </template>
                  <p class="mb-3 text-xs leading-relaxed text-gray-400">
                    {{ currentRequestShape(activeEffect) }}. Empty values mean leave
                    the field absent or blank unless your workflow needs it.
                  </p>
                  <div class="w-full overflow-x-auto">
                    <table class="w-full min-w-[980px] text-xs">
                      <thead>
                        <tr class="border-b border-gray-800 text-gray-400">
                          <th class="px-2 py-2 text-left font-medium whitespace-nowrap">field</th>
                          <th class="px-2 py-2 text-left font-medium whitespace-nowrap">required</th>
                          <th class="px-2 py-2 text-left font-medium whitespace-nowrap">
                            current/example
                          </th>
                          <th class="px-2 py-2 text-left font-medium whitespace-nowrap">
                            allowed values
                          </th>
                          <th class="px-2 py-2 text-left font-medium min-w-[220px]">notes</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr
                          v-for="row in activeParamRows"
                          :key="row.field"
                          class="border-b border-gray-800/80 align-top"
                        >
                          <td class="px-2 py-2 whitespace-nowrap">
                            <code class="text-sky-300">{{ row.field }}</code>
                          </td>
                          <td class="px-2 py-2 whitespace-nowrap">{{ row.required }}</td>
                          <td class="px-2 py-2 break-all">{{ row.current }}</td>
                          <td class="px-2 py-2 break-all">{{ row.options }}</td>
                          <td class="px-2 py-2 break-all text-gray-400">
                            {{ row.note }}
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>

                  <div class="mt-4 space-y-3">
                    <div class="text-sm font-semibold">当前 effect 元数据</div>
                    <dl class="grid grid-cols-1 gap-x-3 gap-y-2 text-sm sm:grid-cols-[160px_1fr]">
                      <dt class="text-gray-400">upload_image_count</dt>
                      <dd class="break-all">
                        {{
                          activeEffect.upload_image_count ||
                          activeEffect.effect_type ||
                          "-"
                        }}
                      </dd>
                      <dt class="text-gray-400">qualities</dt>
                      <dd>
                        <div class="flex flex-wrap gap-1.5">
                          <UBadge
                            v-for="item in list(activeEffect.qualities)"
                            :key="item"
                            color="neutral"
                            variant="subtle"
                            size="xs"
                          >
                            {{ item }}
                          </UBadge>
                          <span
                            v-if="!list(activeEffect.qualities).length"
                            class="text-gray-500"
                          >
                            -
                          </span>
                        </div>
                      </dd>
                      <dt class="text-gray-400">support_duration_seconds</dt>
                      <dd>
                        <div class="flex flex-wrap gap-1.5">
                          <UBadge
                            v-for="item in list(activeEffect.support_duration_seconds)"
                            :key="item"
                            color="neutral"
                            variant="subtle"
                            size="xs"
                          >
                            {{ item }}s
                          </UBadge>
                        </div>
                      </dd>
                      <dt class="text-gray-400">supported_features</dt>
                      <dd>
                        <div class="flex flex-wrap gap-1.5">
                          <UBadge
                            v-for="item in list(activeEffect.supported_features)"
                            :key="item"
                            color="neutral"
                            variant="subtle"
                            size="xs"
                          >
                            {{ item }}
                          </UBadge>
                        </div>
                      </dd>
                      <dt class="text-gray-400">type / type_label</dt>
                      <dd class="break-all">
                        {{ activeEffect.type ?? "" }} /
                        {{ activeEffect.type_label || "" }}
                      </dd>
                      <dt class="text-gray-400">channels</dt>
                      <dd class="break-all">
                        {{
                          textList(activeEffect.channels_zh) ||
                          textList(activeEffect.channels_en)
                        }}
                      </dd>
                    </dl>

                    <div class="text-sm font-semibold">请求体示例</div>
                    <pre
                      class="max-h-80 overflow-auto rounded-lg border border-gray-800 bg-gray-950 p-3 text-xs leading-relaxed"
                    >{{ activeRequestExample }}</pre>
                  </div>
                </UCard>

                <UCard class="bg-gray-900 ring-1 ring-gray-800" :ui="{ body: 'p-4 sm:p-4' }">
                  <template #header>
                    <div class="text-sm font-semibold">主体要求 / Prompt</div>
                  </template>
                  <dl class="grid grid-cols-1 gap-x-3 gap-y-2 text-sm sm:grid-cols-[160px_1fr]">
                    <dt class="text-gray-400">主体要求</dt>
                    <dd class="whitespace-pre-line break-all">
                      {{ activeEffect.topic_requirements || "-" }}
                    </dd>
                    <dt class="text-gray-400">中文 prompt</dt>
                    <dd class="break-all">{{ activeEffect.prompt_zh || "-" }}</dd>
                    <dt class="text-gray-400">英文 prompt</dt>
                    <dd class="break-all">{{ activeEffect.prompt_en || "-" }}</dd>
                  </dl>
                </UCard>

                <UCard class="bg-gray-900 ring-1 ring-gray-800" :ui="{ body: 'p-4 sm:p-4' }">
                  <template #header>
                    <div class="text-sm font-semibold">积分消耗</div>
                  </template>
                  <div v-if="activeCreditRows.length" class="overflow-x-auto">
                    <table class="w-full min-w-[320px] text-sm">
                      <thead>
                        <tr class="border-b border-gray-800 text-gray-400">
                          <th class="px-2 py-2 text-left font-medium">条件</th>
                          <th class="px-2 py-2 text-left font-medium">credits</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr
                          v-for="row in activeCreditRows"
                          :key="row.consume_desc_key"
                          class="border-b border-gray-800/80"
                        >
                          <td class="px-2 py-2">{{ row.consume_desc_key }}</td>
                          <td class="px-2 py-2">{{ row.credit }}</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                  <span v-else class="text-sm text-gray-400">无</span>
                </UCard>

                <UCard class="bg-gray-900 ring-1 ring-gray-800" :ui="{ body: 'p-4 sm:p-4' }">
                  <template #header>
                    <div class="text-sm font-semibold">媒体链接</div>
                  </template>
                  <div class="space-y-2 text-sm">
                    <div
                      v-for="row in mediaLinkRows"
                      :key="row.label"
                      class="grid grid-cols-1 gap-1 sm:grid-cols-[140px_1fr]"
                    >
                      <span class="text-gray-400">{{ row.label }}</span>
                      <a
                        :href="row.url"
                        target="_blank"
                        rel="noreferrer"
                        class="break-all text-sky-300 hover:underline"
                      >
                        {{ row.url }}
                      </a>
                    </div>
                    <span v-if="!mediaLinkRows.length" class="text-gray-400">
                      无
                    </span>
                  </div>
                </UCard>

                <UCard class="bg-gray-900 ring-1 ring-gray-800" :ui="{ body: 'p-4 sm:p-4' }">
                  <template #header>
                    <div class="text-sm font-semibold">原始数据</div>
                  </template>
                  <pre
                    class="max-h-96 overflow-auto rounded-lg border border-gray-800 bg-gray-950 p-3 text-xs leading-relaxed"
                  >{{ JSON.stringify(activeEffect, null, 2) }}</pre>
                </UCard>
              </div>
            </div>
          </div>
        </div>
      </template>
    </UModal>
  </div>
</template>
